import { useState, useCallback, useReducer, useMemo, useEffect } from 'react';

import Heading from 'components/partials/heading/heading';
import Loader from 'components/partials/loader/loader';
import TableComponent from 'components/partials/table-component/table-component';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';

import { getClientReportsTableSettings } from 'containers/admin/clients/client/tabs-components/reports-tab/client-reports-tab-table-settings';

import { useClientReportsList } from 'containers/admin/clients/client/tabs-components/reports-tab/hooks/use-client-reports';
import { useClientProductsList } from 'containers/admin/clients/client/tabs-components/reports-tab/hooks/use-client-products';
import { useCurrentUser } from 'state/current-user/current-user.thunk';

import { usersReducer } from 'containers/admin/reducer';
import { PAGE_CHANGE } from 'containers/admin/actions';

import Icons from 'assets/icons';

import type { SortingState } from '@tanstack/react-table';
import type {
  ClientReportRow,
  ClientProductOption,
} from 'containers/admin/clients/client/tabs-components/reports-tab/client-reports-tab-table-settings';

interface ReportsTabProps {
  clientId: string | number | undefined;
}

interface ReportsReducerState {
  pageIndex: number;
}

const initialState: ReportsReducerState = {
  pageIndex: 0,
};

const ReportsTab = ({ clientId }: ReportsTabProps): React.JSX.Element => {
  const currentUser = useCurrentUser();
  const userId = (currentUser?.data as { oktaUserId?: string } | null | undefined)
    ?.oktaUserId;

  const [state, dispatch] = useReducer(usersReducer, initialState) as [
    ReportsReducerState,
    React.Dispatch<{ type: string; payload?: unknown }>,
  ];

  const [{ pageSize, totalRecords }, setAmount] = useState<{
    pageSize: number;
    totalRecords: number;
  }>({
    pageSize: 10,
    totalRecords: 0,
  });

  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: 'productId', desc: false },
  ]);

  const { pageIndex } = state;

  const queryParams = useMemo(
    () => ({
      pageIndex,
      pageSize,
      sortBy: sortingParams,
    }),
    [pageIndex, pageSize, sortingParams],
  );

  const {
    data: response,
    isLoading,
    isFetching,
    isError,
  } = useClientReportsList({
    userId,
    clientId,
    queryParams,
    enabled: !!userId && !!clientId,
  });

  const { data: products = [] } = useClientProductsList({
    userId,
    clientId,
    enabled: !!userId && !!clientId,
  });

  const data = (response?.data ?? []) as ClientReportRow[];
  const paginationData = response?.pagination;

  useEffect(() => {
    if (paginationData) {
      setAmount({
        pageSize: paginationData.pageSize,
        totalRecords: paginationData.totalRecords,
      });
    }
  }, [paginationData]);

  const handlePagination = useCallback(
    (payload: number) => dispatch({ type: PAGE_CHANGE, payload }),
    [],
  );

  const columns = useMemo(
    () =>
      getClientReportsTableSettings(
        clientId,
        products as ClientProductOption[],
      ),
    [clientId, products],
  );

  const initialLoading = isLoading && !response;
  const tableLoading = isFetching && !initialLoading;

  const renderContent = (): React.JSX.Element => {
    if (initialLoading) {
      return (
        <Loader
          loading={true}
          customClass="!bg-white"
          fullScreen={false}
          overlayClassName="!opacity-0"
        />
      );
    }

    if (data.length > 0 || tableLoading) {
      return (
        <TableComponent
          columns={columns}
          data={data}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPagination={handlePagination}
          setAmount={setAmount}
          sortingParams={sortingParams}
          setSortingParams={setSortingParams}
          totalRecords={totalRecords}
          isLoading={tableLoading}
          customHeight="max-h-[50vh]"
        />
      );
    }

    if (isError) {
      return (
        <EmptySpaceContent
          image={<Icons.EmptyImage customClass="w-[210px] h-[210px]" />}
          text="An error occurred while loading data. Please try again."
        />
      );
    }

    return (
      <EmptySpaceContent
        image={<Icons.EmptyImage customClass="w-[210px] h-[210px]" />}
        text="There are no reports to show yet."
      />
    );
  };

  return (
    <div className="relative">
      <div className="flex justify-between items-center my-5">
        <Heading variant="h2">Reports</Heading>
      </div>

      <div className="relative">{renderContent()}</div>
    </div>
  );
};

export default ReportsTab;
